import { ReactComponent as SalesforceLogo } from '@/assets/images/integrations/salesforce-logo.svg';
import ConnectionStatus from '@/constants/ConnectionStatus';
import IntegrationID from '@/constants/integrations/IntegrationID';
import FeatureFlags from '@/constants/system/FeatureFlags';
import translate from '@/lib/intl/translate';
import type { Integration } from '@/types/Integration';

export const IntegrationNames = Object.freeze({
  [IntegrationID.CLASSY]: translate('INTEGRATIONS_CLASSY'),
  [IntegrationID.CONSTANT_CONTACT]: translate('INTEGRATIONS_CONSTANT_CONTACT'),
  [IntegrationID.GMAIL]: translate('INTEGRATIONS_GMAIL'),
  [IntegrationID.MAILCHIMP]: translate('INTEGRATIONS_MAILCHIMP'),
  [IntegrationID.OUTLOOK]: translate('INTEGRATIONS_OUTLOOK'),
  [IntegrationID.PLEDGE]: translate('INTEGRATIONS_PLEDGE'),
  [IntegrationID.SALESFORCE]: translate('INTEGRATIONS_SALESFORCE'),
  [IntegrationID.STRIPE]: translate('INTEGRATIONS_STRIPE'),
  [IntegrationID.EVENTBRITE]: translate('INTEGRATIONS_EVENTBRITE'),
});

export const IntegrationTypes = Object.freeze({
  CLASSIC: 'classic',
  WORKATO: 'workato',
});

const data: {
  integrations: Integration[];
} = {
  integrations: [
    {
      id: IntegrationID.CLASSY,
      name: IntegrationNames[IntegrationID.CLASSY],
      logo: 'https://images.g2crowd.com/uploads/product/image/large_detail/large_detail_79c51f868a9565dbdd6b67e6199e556d/classy.png',
      shortDescription:
        'Automate your donation information from the Classy online fundraising platform.',
      longDescription:
        'Classy Enables Nonprofits To Build Simple Yet Powerful Online & Mobile Giving Experiences.',
      website: 'https://www.classy.org/',
      type: IntegrationTypes.CLASSIC,
      helperSteps: {
        title: 'How to Find Your Classy Credentials',
        description: null,
        steps: [
          {
            id: 'step-1',
            description: `
<style>
  .container {
     display: flex;
  }
  .two-thirds-left {
     width: 66%;
     padding-right: 32px;
  }
  .one-third-right {
    width: 33%;
    margin-top: -35px;
  }
  @media (max-width: 600px) {
    .container {
      flex-direction: column;
    }
    .two-thirds-left {
      width: 100%;
    }
    .one-third-right {
      width: 85%;
      margin: 16px auto 0;
    }
  }
</style>
<div class="container">
  <div class="two-thirds-left">
    Head over to <a href="www.classy.org" style="color: #00A85D; text-decoration: none;">Classy.org</a>
    and log in to your account. If you are part of multiple organizations, be sure to choose the correct one that you wish to connect with Instil.
  </div>
  <div class="one-third-right">
    <img style="width: 100%;" src="https://i.imgur.com/j0jOexQ.png"/>
  </div>
</div>`,
          },
          {
            id: 'step-2',
            description:
              '<style>.long-image { width: 80%; } @media (max-width: 600px) { .long-image { width: 100% } } </style><div style="display: flex; flex-direction: column"><div>Once logged into Classy your brower’s navigation bar will contain a set of numbers after /admin/. This set of numbers is your Organization ID. Copy this from your Navigation bar to the form field above.</div><div style="display: flex; justify-content: center; padding-top: 12px;"><img class="long-image" src="https://i.imgur.com/grXicsc.png" /></div></div>',
          },
          {
            id: 'step-3',
            description:
              '<style>.container { display: flex; } .two-thirds-left { width: 66%; padding-right: 32px; } .one-third-right { width: 33%; margin-top: -35px; } @media (max-width: 600px) { .container { flex-direction: column; } .two-thirds-left { width: 100%; } .one-third-right { width: 85%; margin: 16px auto 0; } }</style><div class="container"><div class="two-thirds-left">From your Classy account, select the Apps & Extensions link on the bottom left of the main navigation menu.</div><div class="one-third-right"><img style="width: 100%;" src="https://i.imgur.com/bNEyv0T.png"/></div>',
          },
          {
            id: 'step-4',
            description:
              '<style>.container { display: flex; } .two-thirds-left { width: 66%; padding-right: 32px; } .one-third-right { width: 33%; margin-top: -35px; } @media (max-width: 600px) { .container { flex-direction: column; } .two-thirds-left { width: 100%; } .one-third-right { width: 85%; margin: 16px auto 0; } }</style><div class="container"><div class="two-thirds-left">From the Apps & Extensions screen, select “Classy API” to access your API credentials. If you don’t see this option, please contact Classy to enable access.</div><div class="one-third-right"><img style="width: 100%;" src="https://i.imgur.com/FipjMqO.png"/></div>',
          },
          {
            id: 'step-5',
            description:
              '<style>.container { display: flex; } .two-thirds-left { width: 66%; padding-right: 32px; } .one-third-right { width: 33%; margin-top: -35px; } @media (max-width: 600px) { .container { flex-direction: column; } .two-thirds-left { width: 100%; } .one-third-right { width: 85%; margin: 16px auto 0; } }</style><div class="container"><div class="two-thirds-left"><div style="display: flex; flex-direction: column;">Create a new App by filling in the Application Name and Oauth2 Redirect URL with the details found below.</div><div style="background: #F8F9F9; border-radius: 8px; margin-top:16px; padding: 12px; display: flex; justify-content: space-between; flex-wrap: wrap;"><div><div style="font-weight: 600">Application Name</div><div>Instil</div></div><div><div style="font-weight: 600">Oauth2 Redirect URI</div><div>http://instil.io</div></div></div></div><div class="one-third-right"><img style="width: 100%;" src="https://i.imgur.com/2zwgtTE.png"/></div>',
          },
          {
            id: 'step-6',
            description:
              '<style>.long-image { width: 80%; } @media (max-width: 600px) { .long-image { width: 100% } } </style><div style="display: flex; flex-direction: column"><div>Once created, your new app will appear below in the “Your Applications” list. To view your full Client ID and Client Secret, click Edit in the row of your newly created application.</div><div style="display: flex; justify-content: center; padding-top: 12px;"><img class="long-image" src="https://i.imgur.com/jCQXmdq.png" /></div></div>',
          },
          {
            id: 'step-7',
            description:
              '<style>.container { display: flex; } .two-thirds-left { width: 66%; padding-right: 32px; } .one-third-right { width: 33%; margin-top: -35px; } @media (max-width: 600px) { .container { flex-direction: column; } .two-thirds-left { width: 100%; } .one-third-right { width: 85%; margin: 16px auto 0; } }</style><div class="container"><div class="two-thirds-left">Find your Client ID and Client Secret. Copy each value to it’s corosponding field in the form above. Once both have been copied over, save the connection.</div><div class="one-third-right"><img style="width: 100%;" src="https://i.imgur.com/3uL8f5J.png"/></div>',
          },
        ],
      },
    },
    {
      id: IntegrationID.MAILCHIMP,
      name: IntegrationNames[IntegrationID.MAILCHIMP],
      logo: 'https://img.icons8.com/windows/452/mailchimp.png',
      shortDescription:
        'Mailchimp is a marketing automation platform and email marketing service for managing mailing lists and creating email marketing campaigns to send to customers.',
      connectionDescription:
        'Instil has partnered with Workato to support this integration. After clicking Connect, you will be prompted to enter your Mailchimp login and password. You may need to allow pop ups within your browser.',
      website: 'https://mailchimp.com',
      type: IntegrationTypes.WORKATO,
      helperSteps: {
        title: translate('INTEGRATIONS_INSTRUCTIONS'),
        description: null,
        showFor: ConnectionStatus.CONNECTED,
        steps: [
          {
            id: 'step-1',
            description:
              '<div class="flex flex-col md:flex-row"><div class="w-full md:w-2/3 pr-0 md:pr-8">In order for Instil to know which features are available to your Mailchimp account, you will need to set your pricing tier above. To find your current Mailchimp pricing tier, you will need to start by going to mailchimp.com and logging in to your account.</div><div class="w-full md:w-1/3 mt-4 md:-mt-14"><img style="width: 100%;" src="https://res.cloudinary.com/instilio/image/upload/v1/dev/subZkt6dFZsa0xUZFdqbFk0OV94cEYxUQ%3D%3D/ovfrrkx60t7xc2b4sm98"/></div>',
          },
          {
            id: 'step-2',
            description:
              '<div class="flex flex-col md:flex-row"><div class="w-full md:w-2/3 pr-0 md:pr-8">After logging in to your account, click on your profile icon in the lower left of your Mailchimp dashboard. From this menu, select “Pricing Plans”.</div><div class="w-full md:w-1/3 mt-4 md:-mt-14"><img style="width: 100%;" src="https://res.cloudinary.com/instilio/image/upload/v1/dev/subZkt6dFZsa0xUZFdqbFk0OV94cEYxUQ%3D%3D/waei3xkovrlsmgrhk2vo"/></div>',
          },
          {
            id: 'step-3',
            description:
              '<div class="flex flex-col"><div>Look for the pricing tier that says “Your Current Plan”. Once you’ve found it, please set that pricing tier above. In order to take advantage of our dynamic information exporting, you will be required to have either a Standard or Premium tier account. You can still export your constituent data on the Essentials and Free account, but some of the data will be limited.</div><div class="flex justify-center pt-3"><img class="w-full" src="https://res.cloudinary.com/instilio/image/upload/v1/dev/subZkt6dFZsa0xUZFdqbFk0OV94cEYxUQ%3D%3D/hubzthnw0epqtld1qugy" /></div></div>',
          },
        ],
      },
    },
    {
      id: IntegrationID.CONSTANT_CONTACT,
      name: IntegrationNames[IntegrationID.CONSTANT_CONTACT],
      logo: 'https://res.cloudinary.com/instilio/image/upload/v1669681484/Constant_Contact_logo_square-1920w_yxbxof.png',
      shortDescription:
        'Constant Contact is an online marketing tool for creating effective email marketing and other online marketing campaigns to meet organizations’ goals.',
      connectionDescription:
        'Constant Contact has partnered with Workato to support this integration. If you wish to remove Instil’s access to Constant Contact, you will need to go to Constant Contact to remove the connection.',
      website: 'https://www.constantcontact.com/',
      type: IntegrationTypes.WORKATO,
      helperSteps: {
        title: translate('INTEGRATIONS_INSTRUCTIONS'),
        description: null,
        steps: [
          {
            id: 'step-1',
            description:
              '<div class="flex flex-col"><div>Go to <a href="https://app.constantcontact.com/pages/dma/portal/" target="_blank" style="color: #06838C; cursor: pointer;">https://app.constantcontact.com/pages/dma/portal/</a> and sign in using your Constant Contact credentials. This will take you to the <strong>My Applications</strong> section of the Constant Contact API page (pictured below). To start creating your new application, click “New Application”.</div><div class="flex justify-center pt-3"><img class="w-full" src="https://res.cloudinary.com/instilio/image/upload/v1/dev/subZkt6dFZsa0xUZFdqbFk0OV94cEYxUQ%3D%3D/ghihsibwn5qrwclfj0fz" /></div></div>',
          },
          {
            id: 'step-2',
            description:
              '<div class="flex flex-col"><div>You will need to name your new application. We suggest naming it “Instil” so you can easily identify it later.</div><div class="flex justify-center pt-3"><img class="w-full" src="https://res.cloudinary.com/instilio/image/upload/v1/dev/subZkt6dFZsa0xUZFdqbFk0OV94cEYxUQ%3D%3D/rnjboxuehtsfulab2rrh" /></div></div>',
          },
          {
            id: 'step-3',
            description:
              '<div class="flex flex-col"><div>Now that your application has been created, you will need to set your <strong>Redirect URI</strong>. This allows the Instil system to receive information from your Constant Contact account. <br/><br/>You will need to change it to the following: <strong>https://www.workato.com/oauth/callback</strong></div><div class="flex justify-center pt-3"><img class="w-full" src="https://res.cloudinary.com/instilio/image/upload/v1/dev/subZkt6dFZsa0xUZFdqbFk0OV94cEYxUQ%3D%3D/pdrbe4xthcvcrrdaxlq2" /></div></div>',
          },
          {
            id: 'step-4',
            description:
              '<div class="flex flex-col"><div>Next, you will need to generate an <strong>App Secret</strong>. You can begin this process by clicking “Generate Secret” on the previous <strong>App Details</strong> screen. This is a secret key that will be used along with your API Key to verify the authenticity of your Constant Contact account. To continue, click “Generate Secret” on the pop up window.</div><div class="flex justify-center pt-3"><img class="w-full" src="https://res.cloudinary.com/instilio/image/upload/v1/dev/subZkt6dFZsa0xUZFdqbFk0OV94cEYxUQ%3D%3D/ubw2a60eb1zeu1gfwxwo" /></div></div>',
          },
          {
            id: 'step-5',
            description:
              '<div class="flex flex-col"><div>The code shown in the next window is your <strong>App Secret</strong>. Please copy and paste this into the “Client secret” input above. Once you’ve safely copied it over, you can close the <strong>App Secret</strong> window.</div><div class="flex justify-center pt-3"><img class="w-full" src="https://res.cloudinary.com/instilio/image/upload/v1/dev/subZkt6dFZsa0xUZFdqbFk0OV94cEYxUQ%3D%3D/d5rm6mgwkmnwokzqqy6h" /></div></div>',
          },
          {
            id: 'step-6',
            description:
              '<div class="flex flex-col"><div>You will now be back on the <strong>App Details</strong> screen. From this screen, please copy your <strong>API Key</strong> and paste it in the “API key” input above. Once you’ve completed this, please click “Save” on the top right of the <strong>App Details</strong> screen to save all changes we’ve made to your new applicaiton during this process.</div><div class="flex justify-center pt-3"><img class="w-full" src="https://res.cloudinary.com/instilio/image/upload/v1/dev/subZkt6dFZsa0xUZFdqbFk0OV94cEYxUQ%3D%3D/ytzvqicxuegf4kt8zv3o" /></div></div>',
          },
          {
            id: 'step-7',
            description:
              '<div class="flex flex-col"><div>Your new application has now been saved and you are now ready to click “Connect” above and begin harnessing the power of Instil + Constant Contact.</div><div class="flex justify-center pt-3"><img class="w-full" src="https://res.cloudinary.com/instilio/image/upload/v1/dev/subZkt6dFZsa0xUZFdqbFk0OV94cEYxUQ%3D%3D/it3zurzvdfy8ztmsu3fy" /></div></div>',
          },
        ],
      },
    },
    {
      id: IntegrationID.SALESFORCE,
      name: IntegrationNames[IntegrationID.SALESFORCE],
      logo: SalesforceLogo,
      shortDescription:
        'Salesforce is a service focused on fundraising, customer service, marketing automation, e-commerce, and analytics. Requires NonProfit Success pack version 3 or higher.',
      connectionDescription:
        'Organizations with Nonprofit Success pack 3 or greater enabled can support Salesforce data coming into Instil.',
      website: 'https://salesforce.com',
      type: IntegrationTypes.CLASSIC,
      featureFlag: FeatureFlags.SALESFORCE_MILESTONE_1,
    },
    {
      id: IntegrationID.IWAVE,
      name: translate('IWAVE'),
      logo: 'https://res.cloudinary.com/instilio/image/upload/v1699383809/iwave/iwave-logo_mipcf8.png',
      shortDescription: translate('IWAVE_INTEGRATION_DESCRIPTION'),
      connectionDescription: translate('IWAVE_INTEGRATION_DESCRIPTION'),
      website: 'https://iwave.com',
      type: IntegrationTypes.CLASSIC,
      featureFlag: FeatureFlags.IWAVE,
    },
    {
      id: IntegrationID.STRIPE,
      name: IntegrationNames[IntegrationID.STRIPE],
      logo: 'https://res.cloudinary.com/instilio/image/upload/v1/dev/subZkt6dFZsa0xUZFdqbFk0OV94cEYxUQ%3D%3D/oaeindk3x0wlp2vuzuyl',
      shortDescription:
        'Stripe is a leading payment service provider, providing a suite of payment tools for nonprofits.',
      connectionDescription:
        'Stripe is a leading payment service provider, providing a suite of payment tools for nonprofits.',
      website: 'https://stripe.com',
      type: IntegrationTypes.CLASSIC,
      helperSteps: {
        title: translate('INTEGRATIONS_INSTRUCTIONS'),
        steps: [
          {
            id: 'step-1',
            description: `
<div class="flex flex-col md:flex-row">
  <div class="mb-3 md:w-2/3 md:mr-3">
    Sign in to
    <a class="text-tertiary-main" href="https://dashboard.stripe.com/apikeys" target="_blank" noreferrer>your Stripe account</a>.
    If you do not have a Stripe account, you can create one by clicking “Sign
    up” below the sign in box.
  </div>
  <div class="w-1/3">
    <img class="w-full" src="https://res.cloudinary.com/instilio/image/upload/v1651259283/stripe/stripe-signin_hq769l.png"/>
  </div>
</div>
`,
          },
          {
            id: 'step-2',
            description: `
<div class="flex flex-col">
  <div class="mb-3">
    After signing in, you will be directed to your Stripe dashboard. In the
    upper right, click “Developers”.
  </div>
  <img class="w-full" src="https://res.cloudinary.com/instilio/image/upload/v1651259233/stripe/api-key-dashboard_oj4kls.png"/>
</div>
`,
          },
          {
            id: 'step-3',
            description: `
<div class="flex flex-col md:flex-row">
  <div class="mb-3 md:w-2/3 md:mr-3">
    Once you reach the Developer dashboard, click “API keys” from the menu on
    the left side of the screen.
  </div>
  <div class="w-1/3">
    <img class="w-full" src="https://res.cloudinary.com/instilio/image/upload/v1651259361/stripe/stripe-signin-1_dshfwl.png"/>
  </div>
</div>
`,
          },
          {
            id: 'step-4',
            description: `
<div class="flex flex-col">
  <div class="mb-3">
    On the API keys screen, click “Create secret key” on the right side of the
    “Standard keys” box. This will open a window allowing you to create an
    Instil specific API key.
  </div>
  <img class="w-full" src="https://res.cloudinary.com/instilio/image/upload/v1651259179/stripe/api-key-dashboard-1_t2qts4.png"/>
</div>
`,
          },
          {
            id: 'step-5',
            description: `
<div class="flex flex-col md:flex-row">
  <div class="mb-3 md:w-2/3 md:mr-3">
    Choose a name for your new API key. We recommend calling it “Instil” for
    ease of identitifcaiton later. Once you’ve chosen a name, click “Create”.
  </div>
  <div class="w-1/3">
    <img class="w-full" src="https://res.cloudinary.com/instilio/image/upload/v1651259394/stripe/stripe-signin-2_agfska.png"/>
  </div>
</div>
`,
          },
          {
            id: 'step-6',
            description: `
<div class="flex flex-col md:flex-row">
  <div class="mb-3 md:w-2/3 md:mr-3">
    Your new API key will now be generated. It will be a long string of
    characters in the middle of the window. Copy this key and paste it in the
    input at the top of this page.
  </div>
  <div class="md:w-1/3">
    <img class="w-full" src="https://res.cloudinary.com/instilio/image/upload/v1651259430/stripe/stripe-signin-3_x61cr6.png"/>
  </div>
</div>
`,
          },
        ],
      },
    },
    {
      id: IntegrationID.GMAIL,
      name: IntegrationNames[IntegrationID.GMAIL],
      logo: 'https://res.cloudinary.com/instilio/image/upload/v1/dev/subZkt6dFZsa0xUZFdqbFk0OV94cEYxUQ%3D%3D/e1kf1gdfdzx7bz1euj7z',
      shortDescription:
        'With Gmail you can choose to easily log the constituent email communications you wish to track and display them in the Constituent and Home Page Activity feeds.',
      longDescription:
        'Gmail keeps your account & emails encrypted, private and under your control with the largest secure email service in the world.',
      connectionDescription:
        'Connecting Gmail will allow you to select and log constituent email interactions you wish to track and display them in the Constituent Activity and Home Page Activity feeds. Please note that this functionality is limited to paid Google Workspace accounts. Read Permissions are required to be set to <strong>Yes</strong> for emails to come into Instil.',
      website: 'http://gmail.com',
      type: IntegrationTypes.WORKATO,
      isPersonal: true,
    },
    {
      id: IntegrationID.OUTLOOK,
      name: IntegrationNames[IntegrationID.OUTLOOK],
      logo: 'https://res.cloudinary.com/instilio/image/upload/v1/dev/subZkt6dFZsa0xUZFdqbFk0OV94cEYxUQ%3D%3D/y5w9asgmydvysx2jn8jk',
      shortDescription:
        'With Outlook you can choose to easily log the constituent email communications you wish to track and display them in the Constituent and Home Page Activity feeds.',
      longDescription:
        'Microsoft Outlook is a personal information manager software system from Microsoft, available as a part of the Microsoft Office suite.',
      connectionDescription:
        'Connecting Outlook will allow you to select and log constituent email interactions you wish to track and display them in the Constituent Activity and Home Page Activity feeds.',
      website: 'http://outlook.com',
      type: IntegrationTypes.WORKATO,
      isPersonal: true,
    },
    {
      id: IntegrationID.EVENTBRITE,
      name: IntegrationNames[IntegrationID.EVENTBRITE],
      logo: 'https://res.cloudinary.com/instilio/image/upload/v1677719379/eventbrite-seeklogo.com_bccssx.svg',
      shortDescription: translate('INTEGRATIONS_EVENTBRITE_DESCRIPTION'),
      connectionDescription: translate(
        'INTEGRATIONS_EVENTBRITE_CONNECTION_DESCRIPTION',
      ),
      website: 'http://eventbrite.com',
      type: IntegrationTypes.CLASSIC,
      helperSteps: {
        title: translate('INTEGRATIONS_INSTRUCTIONS'),
        description: null,
        steps: [
          {
            id: 'step-1',
            description: translate(
              'INTEGRATIONS_EVENTBRITE_INSTRUCTIONS_STEP_1',
            ),
          },
          {
            id: 'step-2',
            description: translate(
              'INTEGRATIONS_EVENTBRITE_INSTRUCTIONS_STEP_2',
            ),
          },
          {
            id: 'step-3',
            description: translate(
              'INTEGRATIONS_EVENTBRITE_INSTRUCTIONS_STEP_3',
            ),
          },
          {
            id: 'step-4',
            description: translate(
              'INTEGRATIONS_EVENTBRITE_INSTRUCTIONS_STEP_4',
            ),
          },
          {
            id: 'step-5',
            description: translate(
              'INTEGRATIONS_EVENTBRITE_INSTRUCTIONS_STEP_5',
            ),
          },
          {
            id: 'step-6',
            description: translate(
              'INTEGRATIONS_EVENTBRITE_INSTRUCTIONS_STEP_6',
            ),
          },
        ],
      },
    },
  ],
};

export default data;
